import { useState } from 'react';
import { format } from 'date-fns';
import { parseLocalDate, formatLocalDate } from '@/utils/date';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { cn } from '@shared';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import { Calendar } from '@/components/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import type { ReportFilters } from '@/features/reports/utils/exportToExcel';

interface ProjectOption {
  id: string;
  name: string;
}

interface MemberOption {
  id: string;
  name: string;
  email: string;
}

interface ReportFiltersProps {
  filters: ReportFilters;
  onChange: (filters: ReportFilters) => void;
  projects: readonly ProjectOption[];
  members: readonly MemberOption[];
}

const BILLING_STATUS_OPTIONS = [
  { value: 'unbilled', label: 'Unbilled' },
  { value: 'billed', label: 'Billed' },
];

export function ReportFiltersComponent({
  filters,
  onChange,
  projects,
  members,
}: ReportFiltersProps) {
  const updateFilter = <K extends keyof ReportFilters>(
    key: K,
    value: ReportFilters[K],
  ) => {
    onChange({ ...filters, [key]: value });
  };

  const selectedMemberIds = new Set(filters.memberIds ?? []);
  const selectedMemberCount = selectedMemberIds.size;
  const totalMemberCount = members.length;

  const toggleMember = (id: string) => {
    const next = new Set(selectedMemberIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    updateFilter('memberIds', next.size > 0 ? Array.from(next) : null);
  };

  const selectAllMembers = () =>
    updateFilter('memberIds', members.map((member) => member.id));
  const clearAllMembers = () => updateFilter('memberIds', null);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <div className="space-y-2">
        <Label>Start date</Label>
        <DatePicker
          value={filters.startDate}
          onChange={(date) => updateFilter('startDate', date)}
          placeholder="Pick a start date"
          testId="start-date-button"
        />
      </div>

      <div className="space-y-2">
        <Label>End date</Label>
        <DatePicker
          value={filters.endDate}
          onChange={(date) => updateFilter('endDate', date)}
          placeholder="Pick an end date"
          testId="end-date-button"
        />
      </div>

      <div className="space-y-2">
        <Label>Project</Label>
        <Select
          value={filters.projectId || 'all'}
          onValueChange={(value) =>
            updateFilter('projectId', value === 'all' ? null : value)
          }
        >
          <SelectTrigger data-testid="project-select-trigger">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Member</Label>
        <MemberFilter
          members={members}
          selectedMemberIds={selectedMemberIds}
          selectedMemberCount={selectedMemberCount}
          totalMemberCount={totalMemberCount}
          onToggle={toggleMember}
          onSelectAll={selectAllMembers}
          onClearAll={clearAllMembers}
        />
      </div>

      <div className="space-y-2">
        <Label>Billing status</Label>
        <Select
          value={filters.billingStatus || 'all'}
          onValueChange={(value) =>
            updateFilter('billingStatus', value === 'all' ? null : value)
          }
        >
          <SelectTrigger data-testid="billing-status-select-trigger">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {BILLING_STATUS_OPTIONS.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

interface MemberFilterProps {
  members: readonly MemberOption[];
  selectedMemberIds: Set<string>;
  selectedMemberCount: number;
  totalMemberCount: number;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}

function MemberFilter({
  members,
  selectedMemberIds,
  selectedMemberCount,
  totalMemberCount,
  onToggle,
  onSelectAll,
  onClearAll,
}: MemberFilterProps) {
  const [open, setOpen] = useState(false);
  const allSelected = selectedMemberCount === totalMemberCount && totalMemberCount > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between font-normal"
          data-testid="member-filter-button"
        >
          <span>
            {allSelected || selectedMemberCount === 0
              ? `Member (${totalMemberCount})`
              : `Member (${selectedMemberCount}/${totalMemberCount})`}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="start"
        side="bottom"
        data-testid="member-filter-popover"
      >
        <div className="max-h-64 space-y-1 overflow-y-auto p-2">
          {members.map((member) => {
            const checked = selectedMemberIds.has(member.id);
            return (
              <label
                key={member.id}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                data-testid={`member-filter-${member.id}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggle(member.id)}
                />
                <div className="flex-1 truncate">
                  <div className="truncate font-medium">{member.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {member.email}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 border-t px-2 py-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onSelectAll}
            disabled={allSelected}
          >
            Select all
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onClearAll}
            disabled={selectedMemberCount === 0}
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface DatePickerProps {
  value: string | null;
  onChange: (date: string | null) => void;
  placeholder: string;
  testId: string;
}

function DatePicker({ value, onChange, placeholder, testId }: DatePickerProps) {
  const selectedDate = value ? parseLocalDate(value) : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(parseLocalDate(value), 'PPP') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          defaultMonth={selectedDate}
          selected={selectedDate}
          onSelect={(date) => onChange(date ? formatLocalDate(date) : null)}
        />
      </PopoverContent>
    </Popover>
  );
}
